import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { OepService, AuthMethod, AccountAuthResult } from '../../services/oep.service';
import { extractApiError } from '../../../core/utils/api-format';

export interface OepSignatureDialogData {
  organizationInn: string;
  personIdnp: string;
  title?: string;
}

export interface OepSignatureDialogResult {
  success: boolean;
  token?: string;
  data?: AccountAuthResult;
  fieldErrors?: Record<string, string>;
}

@Component({
  selector: 'app-oep-signature-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule
  ],
  template: `
    <div class="dialog-header">
      <h2 mat-dialog-title>{{ data.title || 'Электронная подпись' }}</h2>
      <button mat-icon-button (click)="close()" class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-dialog-content>
      <!-- Step 1: Loading auth methods -->
      @if (currentStep() === 1) {
        @if (loadingMethods()) {
          <div class="loading-container">
            <mat-spinner diameter="40"></mat-spinner>
            <p>Загрузка методов авторизации...</p>
          </div>
        } @else if (authMethods().length > 0) {
          <div class="step-content">
            <p>Выберите способ получения кода подтверждения:</p>
            <mat-radio-group [(ngModel)]="selectedMethod" class="method-group">
              @for (method of authMethods(); track method.authType) {
                <mat-radio-button [value]="method.authType">
                  {{ getMethodLabel(method.authType) }}
                </mat-radio-button>
              }
            </mat-radio-group>
          </div>
        } @else {
          <div class="error-container">
            <mat-icon>error_outline</mat-icon>
            <p>{{ errorMessage() || 'Методы авторизации не найдены' }}</p>
          </div>
        }
      }

      <!-- Step 2: Enter PIN code -->
      @if (currentStep() === 2) {
        <div class="step-content">
          <p>Введите код подтверждения, отправленный на {{ getMethodLabel(selectedMethod) }}:</p>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Код подтверждения</mat-label>
            <input matInput [(ngModel)]="pinCode" maxlength="6" placeholder="000000"
                   (input)="onPinCodeInput($event)" type="tel">
          </mat-form-field>
        </div>
      }

      <!-- Error message -->
      @if (errorMessage()) {
        <div class="error-message">{{ errorMessage() }}</div>
      }
    </mat-dialog-content>

    <mat-dialog-actions class="dialog-actions">
      @if (currentStep() === 1 && authMethods().length > 0) {
        <button mat-button (click)="close()">Отмена</button>
        <button mat-raised-button color="primary"
                (click)="requestPinCode()"
                [disabled]="!selectedMethod || loadingRequest()">
          @if (loadingRequest()) {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            Получить код
          }
        </button>
      }

      @if (currentStep() === 2) {
        <button mat-button (click)="goBack()">Назад</button>
        <button mat-raised-button color="primary"
                (click)="verifyPinCode()"
                [disabled]="!pinCode || pinCode.length < 4 || loadingRequest()">
          @if (loadingRequest()) {
            <mat-spinner diameter="20"></mat-spinner>
          } @else {
            Подтвердить
          }
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px 0;
      gap: 8px;
    }

    .dialog-header h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      word-break: break-word;
      flex: 1;
    }

    .close-button {
      margin: -8px -8px 0 0;
    }

    mat-dialog-content {
      padding: 20px 24px;
      min-height: 150px;
      overflow-x: hidden;
      box-sizing: border-box;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 0;
      gap: 16px;
    }

    .loading-container p {
      color: #666;
      margin: 0;
    }

    .step-content {
      padding: 10px 0;
    }

    .step-content p {
      margin: 0 0 16px;
      color: #333;
    }

    .method-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .full-width {
      width: 100%;
    }

    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 40px 0;
      gap: 12px;
      color: #f44336;
    }

    .error-container mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
    }

    .error-container p {
      white-space: pre-line;
      text-align: center;
      margin: 0;
    }

    .error-message {
      color: #f44336;
      font-size: 14px;
      margin-top: 12px;
      padding: 8px 12px;
      background: #ffebee;
      border-radius: 4px;
    }

    .dialog-actions {
      padding: 16px 24px;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    .dialog-actions button mat-spinner {
      display: inline-block;
    }
  `]
})
export class OepSignatureDialogComponent {
  private oepService = inject(OepService);
  private dialogRef = inject(MatDialogRef<OepSignatureDialogComponent>);
  data = inject<OepSignatureDialogData>(MAT_DIALOG_DATA);

  currentStep = signal(1);
  loadingMethods = signal(true);
  loadingRequest = signal(false);
  authMethods = signal<AuthMethod[]>([]);
  errorMessage = signal<string>('');

  selectedMethod = '';
  pinCode = '';

  constructor() {
    this.loadAuthMethods();
  }

  private loadAuthMethods(): void {
    this.oepService.getUserAuthMethods(this.data.organizationInn, this.data.personIdnp)
      .subscribe({
        next: (response: any) => {
          if (response.success === false) {
            this.handleApiError(response);
            this.loadingMethods.set(false);
            return;
          }

          const data = response.res;
          const activeMethods = data?.userAuthMethodList?.filter((m: AuthMethod) => m.isActive) || [];
          this.authMethods.set(activeMethods);

          if (activeMethods.length === 1) {
            this.selectedMethod = activeMethods[0].authType;
          }

          if (data?.errorMessage) {
            this.errorMessage.set(data.errorMessage);
          }

          this.loadingMethods.set(false);
        },
        error: (error) => {
          console.error('Error loading auth methods:', error);
          if (error.error) {
            this.handleApiError(error.error);
          } else {
            this.errorMessage.set('Ошибка загрузки методов авторизации');
          }
          this.loadingMethods.set(false);
        }
      });
  }

  private handleApiError(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    if (response.res && Array.isArray(response.res) && response.res.length > 0) {
      // Проверяем есть ли ошибки полей personIdnp или organizationInn
      const fieldErrors: Record<string, string> = {};
      let hasFieldErrors = false;

      response.res.forEach(err => {
        if (err.field === 'personIdnp' || err.field === 'organizationInn') {
          fieldErrors[err.field] = err.ru;
          hasFieldErrors = true;
        }
      });

      if (hasFieldErrors) {
        // Закрываем диалог и возвращаем ошибки родительскому компоненту
        this.dialogRef.close({ success: false, fieldErrors } as OepSignatureDialogResult);
        return;
      }

      const messages = response.res.map(err => err.ru).join('\n');
      this.errorMessage.set(messages);
    } else if (response.msg) {
      this.errorMessage.set(response.msg);
    } else {
      this.errorMessage.set('Произошла ошибка');
    }
  }

  getMethodLabel(method: string): string {
    switch (method) {
      case 'email': return 'Email';
      case 'phone': return 'Телефон';
      case 'sms': return 'SMS';
      default: return method;
    }
  }

  requestPinCode(): void {
    this.loadingRequest.set(true);
    this.errorMessage.set('');

    this.oepService.getPinCode({
      method: this.selectedMethod,
      organizationInn: this.data.organizationInn,
      personIdnp: this.data.personIdnp
    }).subscribe({
      next: (response) => {
        this.loadingRequest.set(false);
        if (response.success) {
          this.currentStep.set(2);
        } else {
          this.errorMessage.set(response.msg || response.res?.errorMessage || 'Ошибка отправки кода');
        }
      },
      error: (error) => {
        console.error('Error requesting PIN code:', error);
        // 429 (лимит 7 запросов за 10 минут) и прочие ошибки — текст сервера
        this.errorMessage.set(extractApiError(error?.error, 'Ошибка отправки кода'));
        this.loadingRequest.set(false);
      }
    });
  }

  verifyPinCode(): void {
    this.loadingRequest.set(true);
    this.errorMessage.set('');

    this.oepService.accountAuth({
      byPin: this.pinCode,
      organizationInn: this.data.organizationInn,
      personIdnp: this.data.personIdnp
    }).subscribe({
      next: (response) => {
        this.loadingRequest.set(false);
        if (response.success && response.res) {
          this.dialogRef.close({
            success: true,
            token: response.res.token,
            data: response.res
          } as OepSignatureDialogResult);
        } else {
          this.errorMessage.set(response.msg || 'Ошибка авторизации');
        }
      },
      error: (error) => {
        console.error('Error verifying PIN:', error);
        const msg = error?.error?.errorMessage || error?.error?.msg || 'Ошибка авторизации';
        this.errorMessage.set(msg);
        this.loadingRequest.set(false);
      }
    });
  }

  goBack(): void {
    this.currentStep.set(1);
    this.pinCode = '';
    this.errorMessage.set('');
  }

  onPinCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '');
    if (input.value !== filtered) {
      this.pinCode = filtered;
      input.value = filtered;
    }
  }

  close(): void {
    this.dialogRef.close({ success: false } as OepSignatureDialogResult);
  }
}
